import React, { useState, useEffect } from 'react';
import './uddannelse.css';
import SecondaryButton from '../../components/secondaryButton';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// Funktion til at håndtere både markdown og HTML-indhold
const prepareContent = (content) => {
  if (!content) return '';

  // Hvis teksten allerede er HTML (fra rich text editor)
  if (content.includes('<p>') || content.includes('<h') || content.includes('<ul>')) {
    // Vi beholder HTML som den er, da formatering håndteres korrekt i rich text editor
    return content;
  }

  // Hvis det er markdown eller plain text
  // Konverter overskrifter
  let html = content.replace(/^## (.*?)$/gm, '<h3>$1</h3>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  
  // Del teksten på dobbelte linjeskift, hvilket normalt indikerer nye afsnit
  const paragraphs = html.split(/\n\s*\n/); // Split ved tomme linjer
  html = paragraphs.map(p => {
    const trimmedP = p.trim();
    if (trimmedP === '') return '';
    
    // Håndter lister
    if (trimmedP.includes('<li>')) {
      return `<ul>${trimmedP}</ul>`;
    }
    
    // Håndter overskrifter
    if (trimmedP.includes('<h3>')) {
      return trimmedP;
    }
    
    // Almindelige afsnit - bevar enkelte linjeskift som mellemrum
    return `<p>${trimmedP.replace(/\n/g, ' ')}</p>`;
  }).join('\n\n'); // Tilføj afstand mellem afsnit

  return html;
};

const Uddannelse = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [uddannelser, setUddannelser] = useState([]);
  const [hovedIndhold, setHovedIndhold] = useState({
    title: 'Undervisning og workshops',
    heroImage: '/media/mbtpåhylde.avif',
    textBefore: '',
    textAfter: ''
  });

  useEffect(() => {
    const fetchUddannelser = async () => {
      try {
        // Hent hovedindhold
        const uddannelseDocRef = doc(db, 'content', 'uddannelse');
        const uddannelseDoc = await getDoc(uddannelseDocRef);
        
        if (uddannelseDoc.exists()) {
          const data = uddannelseDoc.data();
          setHovedIndhold({
            title: data.title || 'Undervisning og workshops',
            heroImage: data.heroImage || '/media/mbtpåhylde.avif',
            textBefore: data.textBefore || '',
            textAfter: data.textAfter || ''
          });
        }
        
        // Hent uddannelser
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists() && uddannelserDoc.data().uddannelser) {
          setUddannelser(uddannelserDoc.data().uddannelser);
        }
      } catch (error) {
        console.error('Fejl ved hentning af uddannelsesdata:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUddannelser();
  }, []);

  if (isLoading) {
    return <div className="uddannelse-loading">Indlæser...</div>;
  }

  return (
    <div className="pp-uddannelse">
      <div className="pp-uddannelse__container">
        <h1 className="pp-uddannelse__title">{hovedIndhold.title}</h1>
        
        <div className="pp-uddannelse__hero">
          <div className="pp-uddannelse__hero-billed">
            <img src={hovedIndhold.heroImage} alt="Uddannelse hero" />
          </div>
          
          {hovedIndhold.textBefore && (
            <div 
              className="pp-uddannelse__hero-text"
              dangerouslySetInnerHTML={{ __html: prepareContent(hovedIndhold.textBefore) }}
            />
          )}
        </div>

        <h2 className="pp-uddannelse__section-title">Uddannelser</h2>

        <div className="pp-uddannelse__kurser">
          {uddannelser.map((uddannelse) => (
            <div key={uddannelse.id} className="pp-uddannelse__kort">
              <div className="pp-uddannelse__kort-image">
                <img src={uddannelse.imageUrl} alt={uddannelse.title} />
              </div>
              <div className="pp-uddannelse__kort-content">
                <h3>{uddannelse.title}</h3>
                <p>{uddannelse.description}</p>
                <SecondaryButton href={`/uddannelse/${uddannelse.id}`}>Læs mere</SecondaryButton>
              </div>
            </div>
          ))}
        </div>

        {hovedIndhold.textAfter && (
          <div 
            className="pp-uddannelse__info"
            dangerouslySetInnerHTML={{ __html: prepareContent(hovedIndhold.textAfter) }}
          />
        )}

        {!hovedIndhold.textAfter && (
          <div className="pp-uddannelse__info">
            <p>Hos Psykologisk Perspektiv udbyder vi kurser og uddannelse i Mentaliseringsbaseret terapi til psykologer og andre fagpersoner. Du kan læse om vores kurser som aktuelt udbydes her på hjemmesiden.</p>
            <p>Vi tilbyder også skræddersyede undervisningstilbud til fagpersoner indenfor det socialfaglige og pædagogiske område. Vi kommer meget gerne ud på fx opholdssteder, familiehuse, skoler, hospitalsafdelinger, krisecentre m.v. og underviser/afholder workshops indenfor forskellige emner indenfor mentaliserings baseret behandling med børn, unge og deres familier.</p>
            <p>Vi har afholdt workshops om emner som:</p>
            <ul>
              <li>Samtalepraksis med børn, der har levet med vold</li>
              <li>Mentaliseringsbaseret terapi på opholdssteder</li>
              <li>Mentaliseringsstøtte til forældre</li>
              <li>Fagpersoners egne mentaliseringssvigt og egenomsorg</li>
              <li>Følelsesregulering af vrede børn og unge</li>
              <li>Mentaliseringsbaseret traumeforståelse og -behandling</li>
              <li>Mentaliseringsbaseret familieterapi</li>
              <li>Mentalisering i pædagogisk praksis</li>
            </ul>
            <p>Vores undervisning er praktisk orienteret og kan tilpasses mange niveauer og behov. Vi holder meget af at undervise i virkelighedsnær praksis med elementer af "learning by doing", som kan omsættes direkte til deltagernes hverdag og arbejdsopgaver. Uanset om du er ny i mentalisering eller ønsker at styrke dine færdigheder, tilbyder vi et undervisningstilbud, der gør en forskel.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Uddannelse;
